import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { BreadcrumbsV2, DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import {
  MoreContent,
  ParagraphsRenderer,
  QuickLinks,
  RelatedContent,
} from '../../../../components';

import {
  RELATED_ARTICLES_HITS_PER_PAGE,
  RELATED_PROJECTS_HITS_PER_PAGE,
  RELATED_TEAM_HITS_PER_PAGE,
  RELATED_EXPERTISES_HITS_PER_PAGE,
  MORE_DEPARTMENTS_HITS_PER_PAGE,
  RELATED_IMIS_PUBLICATIONS_HITS_PER_PAGE,
  APP_OPTIONS,
} from '../../../../config/constants';
import type { TaxonomyDepartment } from '../../../../types/uncinc_profile/taxonomy';

import './style.scss';

interface DepartmentProps {
  entity: ImmutableEntity<TaxonomyDepartment>;
  className?: string;
}

const TaxonomyDepartmentPage: React.FC<DepartmentProps> = ({ entity, className }) => {
  const { t } = useTranslation();

  const department = entity.toJS();
  const tid = department.tid[0].value;
  const langcode = department.langcode[0].value;
  const title = department.name[0].value;
  const body = department.description[0]?.processed;
  const media = department.field_media_item[0];
  const caption = media?.entity?.field_caption[0]?.value;
  const paragraphs = department.field_paragraphs;

  let defaultFunctionGroupFilterId = 0;
  if (department.field_abbreviation[0].value === 'NMF') {
    defaultFunctionGroupFilterId = 5;
  }
  if (department.field_abbreviation[0].value === 'GES') {
    defaultFunctionGroupFilterId = 9;
  }

  const classes = classNames('Taxonomy', 'TaxonomyDepartment', className);

  const quickLinks = [
    {
      title: t('components.quicklinks.areas_of_expertise'),
      link: '#areas-of-expertise',
      className: 'quicklink__areas-of-expertise',
    },
    {
      title: t('components.quicklinks.team'),
      link: '#team',
      className: 'quicklink__team',
    },
    {
      title: t('components.quicklinks.news'),
      link: '#articles',
      className: 'quicklink__news',
    },
    {
      title: t('components.quicklinks.projects'),
      link: '#projects',
      className: 'quicklink__projects',
    },
    {
      title: t('components.quicklinks.publications'),
      link: '#publications',
      className: 'quicklink__publications',
    },
  ];

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="TaxonomyDepartment__hero">
        <Container>
          <div className="row">
            <div className="col-12">
              <BreadcrumbsV2 showHomeIcon={false} />
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-8">
              <h1 className="TaxonomyDepartment__title" itemProp="title">
                {title}
              </h1>

              {body && (
                <WysiwygField
                  className="TaxonomyDepartment__body body--large"
                  property="schema:text"
                  html={body}
                />
              )}
            </div>
            <div className="col-12 col-md-4 col-lg-3 offset-lg-1">
              <div className="TaxonomyDepartment__quicklinks">
                <QuickLinks quickLinks={quickLinks} />
              </div>
            </div>
            {media && (
              <div className="col-12">
                <div className="TaxonomyDepartment__media-wrapper">
                  <DynamicMediaItem
                    className="TaxonomyDepartment__media"
                    media={media}
                    imageAspectRatio="2_1"
                  />
                  {/* We render the caption outside, to prevent layout shifting */}
                  {caption && (
                    <p className="caption">{caption}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>

      <ParagraphsRenderer
        className="TaxonomyDepartment__paragraphs"
        paragraphs={paragraphs}
        isWide
      />

      <div className="FixedReferenceBlocks">
        <Container>
          <div className="row">
            <div className="col-12 col-md-10">
              <div className="TaxonomyDepartment__areas-of-expertise">
                <div id="areas-of-expertise" className="TaxonomyDepartment__anchor" />
                <RelatedContent
                  title={t('taxonomies.expertises.title_overview')}
                  contentType="expertises"
                  hitsPerPage={RELATED_EXPERTISES_HITS_PER_PAGE}
                  filterNames={['taxonomy_department_tid']}
                  filterValues={[tid]}
                  langcode={langcode}
                />
              </div>

              <div className="TaxonomyDepartment__team">
                <div id="team" className="TaxonomyDepartment__anchor" />
                <RelatedContent
                  title={t('content_types.profile.related_content')}
                  contentType="profile"
                  hitsPerPage={RELATED_TEAM_HITS_PER_PAGE}
                  filterNames={['profile_departments_tid']}
                  filterValues={[tid]}
                  langcode={langcode}
                  facets={[{
                    type: 'menu',
                    attribute: 'function_category',
                    // Set default value to the first value of the profile function group.
                    defaultValue: String(APP_OPTIONS.parameters.profileFunctionGroupWeights[defaultFunctionGroupFilterId]?.id),
                  }]}
                />
              </div>
              <div className="TaxonomyDepartment__articles">
                <div id="articles" className="TaxonomyDepartment__anchor" />
                <RelatedContent
                  title={t('content_types.article.related_content')}
                  contentType="article"
                  hitsPerPage={RELATED_ARTICLES_HITS_PER_PAGE}
                  filterNames={['departments_tid']}
                  filterValues={[tid]}
                  langcode={langcode}
                />
              </div>
              <div className="TaxonomyDepartment__projects">
                <div id="projects" className="TaxonomyDepartment__anchor" />
                <RelatedContent
                  title={t('content_types.project.related_content')}
                  contentType="project"
                  hitsPerPage={RELATED_PROJECTS_HITS_PER_PAGE}
                  filterNames={['departments_tid']}
                  filterValues={[tid]}
                  langcode={langcode}
                />
              </div>
              <div className="TaxonomyDepartment__publications">
                <div id="publications" className="TaxonomyDepartment__anchor" />
                <RelatedContent
                  title={t('custom_entities.nioz_publication_imis.related_content')}
                  contentType="nioz_publication_imis"
                  hitsPerPage={RELATED_IMIS_PUBLICATIONS_HITS_PER_PAGE}
                  filterNames={['imis_publication_departments_tid']}
                  filterValues={[tid]}
                  langcode={langcode}
                  facets={[{ attribute: 'imis_publication_year' }]}
                  withSearchbar
                />
              </div>
              <div className="TaxonomyDepartment__other-departments">
                <div id="departments" className="TaxonomyDepartment__anchor" />
                <MoreContent
                  title={t('taxonomies.departments.other_title')}
                  contentType="departments"
                  hitsPerPage={MORE_DEPARTMENTS_HITS_PER_PAGE}
                  filterName="tid"
                  filterValue={tid}
                  langcode={langcode}
                  buttonUrl={t('taxonomies.departments.other_button_url')}
                  buttonText={t('taxonomies.departments.other_button_text')}
                />
              </div>
            </div>
          </div>
        </Container>
      </div>
    </article>
  );
};

export default TaxonomyDepartmentPage;
